/**
 * Google Analytics 4 helper — thin wrapper around gtag().
 *
 * Custom events tracked:
 *   - login                  (method: google | gitlab | magic_link)
 *   - magic_link_requested   (email domain)
 *   - invitation_sent        (role, method: org | project)
 *   - invitation_accepted    (method: oauth | direct)
 *   - cli_auth_approved
 *   - base_files_uploaded    (type: db | files, project)
 *   - deploy_triggered       (project, preview, source: ui | rebuild)
 *   - deploy_logs_viewed     (project, preview, success)
 *   - deploy_logs_time       (project, preview, seconds)
 *   - preview_action         (action: start | stop | restart | rebuild)
 *   - drush_uli              (project, preview)
 *   - terminal_opened        (project, preview)
 *   - preview_ssh            (project, preview)
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export const GA_ID = process.env.NEXT_PUBLIC_GA_ID || '';

function gtag(...args: unknown[]) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
}

/** Track a custom event. */
export function trackEvent(name: string, params?: Record<string, unknown>) {
  gtag('event', name, params);
}

// --- Auth ---

export function trackLogin(method: 'google' | 'gitlab' | 'magic_link') {
  trackEvent('login', { method });
}

export function trackMagicLinkRequested(email: string) {
  const domain = email.split('@')[1] || 'unknown';
  trackEvent('magic_link_requested', { email_domain: domain });
}

// --- Invitations ---

export function trackInvitationSent(role: string, scope: 'org' | 'project') {
  trackEvent('invitation_sent', { role, scope });
}

export function trackInvitationAccepted(method: 'oauth' | 'direct') {
  trackEvent('invitation_accepted', { method });
}

// --- CLI ---

export function trackCliAuthApproved() {
  trackEvent('cli_auth_approved');
}

// --- Base files ---

export function trackBaseFilesUploaded(type: 'db' | 'files', project: string) {
  trackEvent('base_files_uploaded', { file_type: type, project });
}

// --- Deploys ---

export function trackDeployTriggered(project: string, preview: string, source: 'ui' | 'rebuild') {
  trackEvent('deploy_triggered', { project, preview, source });
}

export function trackDeployLogsViewed(project: string, preview: string, success: boolean) {
  trackEvent('deploy_logs_viewed', { project, preview, success });
}

export function trackDeployLogsTime(project: string, preview: string, seconds: number) {
  trackEvent('deploy_logs_time', { project, preview, value: seconds });
}

// --- Preview actions ---

export function trackPreviewAction(action: string, project: string, preview: string) {
  trackEvent('preview_action', { action, project, preview });
}

export function trackDrushUli(project: string, preview: string) {
  trackEvent('drush_uli', { project, preview });
}

export function trackTerminalOpened(project: string, preview: string) {
  trackEvent('terminal_opened', { project, preview });
}

export function trackPreviewSsh(project: string, preview: string) {
  trackEvent('preview_ssh', { project, preview });
}
